import GlassCard from '../ui/glass/components/GlassCard';

import React, { useState, useEffect } from 'react';
import { db, functions } from '../firebase';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { Box, Typography, Grid, Switch, FormControlLabel, TextField, Button, CircularProgress, Alert, Divider } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import SmsIcon from '@mui/icons-material/Sms';
import PageHeader from '../components/common/PageHeader';
import { Loading } from '../components/common/Loading';

const SmsYonetimi = () => {
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [testMessage, setTestMessage] = useState('');
    const [testResult, setTestResult] = useState({ status: '', message: '' });
    const [isTesting, setIsTesting] = useState(false);

    // Netgsm API Ayarları
    const [netgsmSettings, setNetgsmSettings] = useState({
        netgsmUser: '',
        netgsmPassword: '',
        netgsmHeader: ''
    });
    const [isSavingApi, setIsSavingApi] = useState(false);

    useEffect(() => {
        const docRef = doc(db, 'settings', 'sms');
        const unsubscribe = onSnapshot(docRef, (docSnap) => {
            if (docSnap.exists()) {
                const existingSettings = docSnap.data();
                const defaultTriggers = getInitialTriggers();
                const mergedSettings = {
                    ...existingSettings,
                    triggers: {
                        ...defaultTriggers,
                        ...existingSettings.triggers,
                    }
                };
                setSettings(mergedSettings);
            } else {
                setSettings({ triggers: getInitialTriggers() });
            }
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    // Netgsm API ayarlarını yükle
    useEffect(() => {
        const docRef = doc(db, 'settings', 'sms_api');
        const unsubscribe = onSnapshot(docRef, (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                setNetgsmSettings({
                    netgsmUser: data.netgsmUser || '',
                    netgsmPassword: data.netgsmPassword || '',
                    netgsmHeader: data.netgsmHeader || ''
                });
            }
        });
        return () => unsubscribe();
    }, []);

    const getInitialTriggers = () => ({
        workOrderInProgress: {
            enabled: false,
            template: "Sayın {musteriAdi}, {cihazSeriNumarasi} seri numaralı cihazınız işleme alınmıştır. Gelişmeler hakkında sizi bilgilendireceğiz. Digitalpedi Medikal"
        },
        workOrderReady: {
            enabled: true,
            template: "Sayın {musteriAdi}, {cihazSeriNumarasi} seri numaralı cihazınızın işlemi tamamlanmış ve teslime hazırdır. Digitalpedi Medikal"
        },
        appointmentCreated: {
            enabled: false,
            template: "Sayın {musteriAdi}, {randevuTarihi} tarihli randevunuz başarıyla oluşturulmuştur. Digitalpedi Medikal"
        },
        appointmentReminder: {
            enabled: false,
            template: "Sayın {musteriAdi}, {randevuTarihi} tarihindeki randevunuzu hatırlatırız. Digitalpedi Medikal"
        },
        paymentDueDate: {
            enabled: false,
            template: "Sayın {musteriAdi}, {vadeTarihi} tarihli ve {tutar} tutarındaki planlanmış ödemenizin vadesi gelmiştir. Bilgilerinize. Digitalpedi Medikal"
        },
        maintenanceReminder: {
            enabled: false,
            template: "Sayın {musteriAdi}, {marka} {model} marka cihazınızın periyodik bakım zamanı gelmiştir ({bakimTarihi}). Randevu için bizimle iletişime geçiniz. Digitalpedi Medikal"
        },
        missedMaintenance: {
            enabled: false,
            template: "Sayın {musteriAdi}, {marka} {model} marka cihazınızın periyodik bakım tarihi geçmiştir. Lütfen en kısa sürede randevu alınız. Digitalpedi Medikal"
        }
    });

    const handleSwitchChange = (trigger) => {
        setSettings(prev => ({
            ...prev,
            triggers: {
                ...prev.triggers,
                [trigger]: {
                    ...(prev.triggers[trigger] || { template: getInitialTriggers()[trigger].template }),
                    enabled: !prev.triggers[trigger]?.enabled
                }
            }
        }));
    };

    const handleTemplateChange = (trigger, value) => {
        setSettings(prev => ({
            ...prev,
            triggers: {
                ...prev.triggers,
                [trigger]: {
                    ...(prev.triggers[trigger] || { enabled: false }),
                    template: value
                }
            }
        }));
    };

    const handleSaveSettings = async () => {
        setIsSaving(true);
        try {
            await setDoc(doc(db, 'settings', 'sms'), settings, { merge: true });
            alert('Ayarlar başarıyla kaydedildi!');
        } catch (error) {
            console.error("Ayarlar kaydedilirken hata:", error);
            alert('Ayarlar kaydedilirken bir hata oluştu.');
        } finally {
            setIsSaving(false);
        }
    };

    // Netgsm API ayarlarını kaydet
    const handleSaveApiSettings = async () => {
        setIsSavingApi(true);
        try {
            await setDoc(doc(db, 'settings', 'sms_api'), netgsmSettings, { merge: true });
            alert('Netgsm API ayarları başarıyla kaydedildi!');
        } catch (error) {
            console.error("API ayarları kaydedilirken hata:", error);
            alert('API ayarları kaydedilirken bir hata oluştu.');
        } finally {
            setIsSavingApi(false);
        }
    };

    const handleSendTestSms = async () => {
        if (!testMessage) {
            alert('Lütfen test için bir mesaj girin.');
            return;
        }
        setIsTesting(true);
        setTestResult({ status: '', message: '' });
        try {
            const sendTestSms = httpsCallable(functions, 'sendTestSms');
            const result = await sendTestSms({ message: testMessage });
            setTestResult({ status: 'success', message: result.data.message });
        } catch (error) {
            console.error("Test SMS gönderilirken hata:", error);
            setTestResult({ status: 'error', message: error.message });
        } finally {
            setIsTesting(false);
        }
    };

    if (loading || !settings) {
        return <Loading message="SMS ayarları yükleniyor..." />;
    }

    return (
        <Box sx={{ p: { xs: 1, md: 1 } }}>
            <PageHeader
                title="SMS Yönetim Paneli"
                subtitle="Otomatik bildirim kuralları ve manuel SMS gönderimi."
                icon={<SmsIcon />}
            />

            {/* Netgsm API Ayarları */}
            <GlassCard sx={{ mb: 2, p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <SettingsIcon color="primary" />
                    <Typography variant="h6">Netgsm API Ayarları</Typography>
                </Box>
                <Alert severity="info" sx={{ mb: 2 }}>
                    Netgsm panelinden aldığınız API bilgilerini buraya girin. Bu bilgiler SMS gönderimi için gereklidir.
                </Alert>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            label="Kullanıcı Numarası"
                            value={netgsmSettings.netgsmUser}
                            onChange={(e) => setNetgsmSettings(prev => ({ ...prev, netgsmUser: e.target.value }))}
                            placeholder="8505xxxxxx"
                            helperText="Netgsm hesap numarasınız"
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            type="password"
                            label="API Şifresi"
                            value={netgsmSettings.netgsmPassword}
                            onChange={(e) => setNetgsmSettings(prev => ({ ...prev, netgsmPassword: e.target.value }))}
                            helperText="Netgsm API şifreniz"
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            label="SMS Başlığı"
                            value={netgsmSettings.netgsmHeader}
                            onChange={(e) => setNetgsmSettings(prev => ({ ...prev, netgsmHeader: e.target.value }))}
                            placeholder="FIRMA ADI"
                            helperText="Netgsm panelinden onaylı başlık"
                        />
                    </Grid>
                </Grid>
                <Box sx={{ mt: 2, textAlign: 'right' }}>
                    <Button variant="contained" color="primary" onClick={handleSaveApiSettings} disabled={isSavingApi}>
                        {isSavingApi ? <CircularProgress size={24} /> : 'API Ayarlarını Kaydet'}
                    </Button>
                </Box>
            </GlassCard>

            <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                    <GlassCard sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>Otomatik Bildirim Ayarları</Typography>

                        <Box sx={{ my: 2 }}>
                            <FormControlLabel control={<Switch checked={settings.triggers.workOrderInProgress?.enabled || false} onChange={() => handleSwitchChange('workOrderInProgress')} />} label="Cihaz İşleme Alındığında" />
                            <TextField fullWidth multiline rows={3} label="Mesaj Şablonu" value={settings.triggers.workOrderInProgress?.template || ''} onChange={(e) => handleTemplateChange('workOrderInProgress', e.target.value)} helperText="Kullanılabilir değişkenler: {musteriAdi}, {cihazSeriNumarasi}" sx={{ mt: 1 }} />
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Box sx={{ my: 2 }}>
                            <FormControlLabel control={<Switch checked={settings.triggers.workOrderReady?.enabled || false} onChange={() => handleSwitchChange('workOrderReady')} />} label="Cihaz Teslime Hazır Olduğunda" />
                            <TextField fullWidth multiline rows={3} label="Mesaj Şablonu" value={settings.triggers.workOrderReady?.template || ''} onChange={(e) => handleTemplateChange('workOrderReady', e.target.value)} helperText="Kullanılabilir değişkenler: {musteriAdi}, {cihazSeriNumarasi}" sx={{ mt: 1 }} />
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Box sx={{ my: 2 }}>
                            <FormControlLabel control={<Switch checked={settings.triggers.appointmentCreated?.enabled || false} onChange={() => handleSwitchChange('appointmentCreated')} />} label="Yeni Randevu Oluşturulduğunda" />
                            <TextField fullWidth multiline rows={3} label="Mesaj Şablonu" value={settings.triggers.appointmentCreated?.template || ''} onChange={(e) => handleTemplateChange('appointmentCreated', e.target.value)} helperText="Kullanılabilir değişkenler: {musteriAdi}, {randevuTarihi}" sx={{ mt: 1 }} />
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Box sx={{ my: 2 }}>
                            <FormControlLabel control={<Switch checked={settings.triggers.appointmentReminder?.enabled || false} onChange={() => handleSwitchChange('appointmentReminder')} />} label="Randevu Hatırlatma (1 Gün Önce)" />
                            <TextField fullWidth multiline rows={3} label="Mesaj Şablonu" value={settings.triggers.appointmentReminder?.template || ''} onChange={(e) => handleTemplateChange('appointmentReminder', e.target.value)} helperText="Kullanılabilir değişkenler: {musteriAdi}, {randevuTarihi}" sx={{ mt: 1 }} />
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Box sx={{ my: 2 }}>
                            <FormControlLabel control={<Switch checked={settings.triggers.paymentDueDate?.enabled || false} onChange={() => handleSwitchChange('paymentDueDate')} />} label="Vadeli Ödeme Gününde Hatırlatma" />
                            <TextField fullWidth multiline rows={3} label="Mesaj Şablonu" value={settings.triggers.paymentDueDate?.template || ''} onChange={(e) => handleTemplateChange('paymentDueDate', e.target.value)} helperText="Değişkenler: {musteriAdi}, {vadeTarihi}, {tutar}" sx={{ mt: 1 }} />
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Box sx={{ my: 2 }}>
                            <FormControlLabel control={<Switch checked={settings.triggers.maintenanceReminder?.enabled || false} onChange={() => handleSwitchChange('maintenanceReminder')} />} label="Periyodik Bakım Hatırlatma (7, 3, 1 Gün Önce ve Gününde)" />
                            <TextField fullWidth multiline rows={3} label="Mesaj Şablonu" value={settings.triggers.maintenanceReminder?.template || ''} onChange={(e) => handleTemplateChange('maintenanceReminder', e.target.value)} helperText="Değişkenler: {musteriAdi}, {marka}, {model}, {bakimTarihi}" sx={{ mt: 1 }} />
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Box sx={{ my: 2 }}>
                            <FormControlLabel control={<Switch checked={settings.triggers.missedMaintenance?.enabled || false} onChange={() => handleSwitchChange('missedMaintenance')} />} label="Kaçırılan Bakım Hatırlatma (3 ve 7 Gün Sonra)" />
                            <TextField fullWidth multiline rows={3} label="Mesaj Şablonu" value={settings.triggers.missedMaintenance?.template || ''} onChange={(e) => handleTemplateChange('missedMaintenance', e.target.value)} helperText="Değişkenler: {musteriAdi}, {marka}, {model}" sx={{ mt: 1 }} />
                        </Box>

                        <Box sx={{ mt: 3, textAlign: 'right' }}>
                            <Button variant="contained" onClick={handleSaveSettings} disabled={isSaving}>
                                {isSaving ? <CircularProgress size={24} /> : 'Ayarları Kaydet'}
                            </Button>
                        </Box>
                    </GlassCard>
                </Grid>

                <Grid item xs={12} md={4}>
                    <GlassCard sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>Manuel SMS Gönderimi</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Müşteriye özel SMS göndermek için kullanabilirsiniz.
                        </Typography>
                        <TextField fullWidth label="Telefon Numarası (5xxxxxxxxx)" value={testMessage} onChange={(e) => setTestMessage(e.target.value)} sx={{ mb: 2 }} placeholder="5xxxxxxxxx" />
                        <TextField fullWidth multiline rows={4} label="Mesaj İçeriği" value={testMessage} onChange={(e) => setTestMessage(e.target.value)} sx={{ mb: 2 }} />
                        <Button fullWidth variant="contained" onClick={handleSendTestSms} disabled={isTesting}>
                            {isTesting ? <CircularProgress size={24} /> : 'SMS Gönder'}
                        </Button>
                        {testResult.message && (
                            <Alert severity={testResult.status} sx={{ mt: 2 }}>{testResult.message}</Alert>
                        )}

                        <Divider sx={{ my: 3 }} />

                        <Typography variant="h6" gutterBottom>Test Gönderimi</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Bu panelden gönderilen tüm test SMS'leri sistemde kayıtlı olan yönetici telefon numarasına gider.
                        </Typography>
                        <TextField fullWidth multiline rows={4} label="Test Mesajı İçeriği" value={testMessage} onChange={(e) => setTestMessage(e.target.value)} sx={{ mb: 2 }} />
                        <Button fullWidth variant="outlined" onClick={handleSendTestSms} disabled={isTesting}>
                            {isTesting ? <CircularProgress size={24} /> : 'Test SMS\'i Gönder'}
                        </Button>
                    </GlassCard>
                </Grid>
            </Grid>
        </Box>
    );
};

export default SmsYonetimi;